/**
 * เส้นทางหลัก — **สร้างจาก `lib/routes.ts` ไม่ใช่เขียนมือทีละอัน**
 *
 * เด็กเข้าได้เฉพาะ /learn และ /quiz · ผู้ปกครองเข้าได้ /parent
 * ยังไม่ได้เข้าใช้งาน → ไปหน้า Gate
 *
 * ★ ทำไมต้องสร้างจากตาราง: e2e ไล่ตารางเดียวกันเพื่อยืนยันว่า **ทุกหน้า**
 *   มีทางออกที่เด็กกดเองได้ ถ้า router มีหน้าที่ตารางไม่รู้จัก test จะไม่เคย
 *   เปิดหน้านั้นเลย แล้วหน้าที่ไม่มีทางออกจะหลุดไปถึงเด็กโดยไม่มีใครรู้
 */
import { useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { Center, Shell } from '@/components/Shell'
import { translate as t } from '@/i18n'
import { ROUTES, type PageName } from '@/lib/routes'
import { useSession } from '@/lib/session'
import { GatePage } from '@/pages/GatePage'
import { ParentPage } from '@/pages/ParentPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { QuizPage } from '@/pages/QuizPage'
import { ResultPage } from '@/pages/ResultPage'
import { SubjectPage } from '@/pages/SubjectPage'
import { TopicPage } from '@/pages/TopicPage'

function NotFoundPage(): JSX.Element {
  return (
    <Shell>
      <Center>
        <h1>{t('error.notFound')}</h1>
      </Center>
    </Shell>
  )
}

const PAGES: Record<PageName, JSX.Element> = {
  gate: <GatePage />,
  register: <RegisterPage />,
  subjects: <SubjectPage />,
  topics: <TopicPage />,
  quiz: <QuizPage />,
  result: <ResultPage />,
  parent: <ParentPage />,
}

function Guard({ children }: { children: JSX.Element }): JSX.Element {
  const { ready, me } = useSession()
  if (!ready) {
    return (
      <Shell>
        <Center>
          <p>{t('state.loading')}</p>
        </Center>
      </Shell>
    )
  }
  if (!me) return <Navigate to="/enter" replace />
  return children
}

export function App(): JSX.Element {
  const bootstrap = useSession((s) => s.bootstrap)
  useEffect(() => {
    void bootstrap()
  }, [bootstrap])

  return (
    <Routes>
      <Route index element={<Navigate to="/learn" replace />} />
      {ROUTES.map((route) => {
        const page = PAGES[route.page]
        return (
          <Route
            key={route.path}
            path={route.path}
            element={route.guarded ? <Guard>{page}</Guard> : page}
          />
        )
      })}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
